// ═══════════════════════════════════════════════════════════════════════════════
//  src/advanced/situacionTextos.js
//  LOS TEXTOS DE LA SITUACIÓN · español e inglés
//
//  Rob, sobre el coach: «si nuestra selección es inglés, también los textos
//  deberíamos de verlos en inglés». Aquí llegaba igual de roto y peor: la sala
//  pedía el catálogo con "es" fijo y el juez tenía escrito en el prompt que
//  dictaminara en español, así que el VEREDICTO —que es la carga útil del
//  formato, lo que el alumno viene a leer— salía en el idioma equivocado.
//
//  Y le tocaba justo al alumno de este formato: La Situación es B2–C2, y por la
//  regla de niveles C1 y C2 entrenan en inglés. El alumno POR DEFECTO era el que
//  lo recibía mal.
//
//  ── QUIÉN DECIDE EL IDIOMA ──────────────────────────────────────────────────
//  El servidor, y sólo el servidor. `GET /situacion/opciones` devuelve el idioma
//  ya resuelto junto con las etiquetas, y esta pantalla lo obedece. Si lo
//  dedujera por su cuenta habría dos reglas —una aquí y otra allí— y el día que
//  discreparan, el alumno leería media pantalla en cada idioma.
//
//  ── POR QUÉ UN FICHERO Y NO UNA LIBRERÍA DE IDIOMAS ─────────────────────────
//  Es el mismo precedente que `coachTextos.js`: esto es UNA pantalla, y meter
//  i18n entera para dos columnas de texto es traer un camión para llevar una
//  caja. El día que NSL se traduzca de verdad, esto se sustituye sin tocar la
//  sala: `textos()` seguirá devolviendo lo mismo.
//
//  ⚠️  PENDIENTE, y conviene saberlo: la sala que rodea a este formato —las
//      siete tarjetas de Alto Rendimiento— sigue en español. Un alumno en
//      inglés entra por una tarjeta en español. Es mecánico de arreglar ahora
//      que el patrón existe, pero es otra pieza.
// ═══════════════════════════════════════════════════════════════════════════════

const es = {
  formato: 'La situación',
  salir: '← Salir',
  volver: 'Volver a la sala',
  preparando: 'Preparando la escena…',

  // ── Las perillas ──────────────────────────────────────────────────────────
  monta: 'Monta tu escena',
  montaTexto: 'Elige dónde trabajas y qué conversación te toca tener. Al final no se te va a calificar el inglés: se te va a decir si',
  montaTextoFuerte: 'lo conseguiste',
  tuSector: 'Tu sector',
  laConversacion: 'La conversación',
  cuantoAprieta: 'Cuánto te aprieta',
  suAcento: 'Su acento',
  exigenciaPorque1: 'Esto cambia al otro, no tu nivel. En',
  exigenciaPorqueDuro: 'duro',
  exigenciaPorque2: 'te interrumpe y sólo cede si de verdad lo convences.',
  entrar: 'Entrar a la conversación →',

  // `n` es el número de escenas, que lo cuenta la pantalla a partir del
  // catálogo: si un día se añade un sector, esta frase se actualiza sola.
  catalogo: (n) => `${n} escenas distintas, y ninguna la inventa una IA: se arman `
    + 'de piezas fijas, así que la misma configuración da la misma escena hoy y '
    + 'en un mes. Tu elección queda guardada para la próxima vez.',
  sinMicro: 'Este navegador no reconoce voz, así que vas a escribir tus turnos. '
    + 'El formato funciona igual, pero hablando entrena mucho más — en Chrome o '
    + 'Edge tendrás micrófono.',

  // ── La conversación ───────────────────────────────────────────────────────
  contestar: '🎤 Contestar',
  yaDije: '■ Ya dije lo mío',
  enviar: 'Enviar mi turno',
  escribeAqui: 'Escribe tu turno en inglés…',
  teEscucho: 'Te escucho…',
  cerrar: 'Cerrar y ver si lo logré',
  sueloListo: '✓ Ya hay conversación suficiente para dictaminar. Cierra cuando quieras.',
  suelo: (turnos, palabras) => {
    const trozos = [];
    if (turnos) trozos.push(`${turnos} ${turnos === 1 ? 'turno tuyo' : 'turnos tuyos'}`);
    if (palabras) trozos.push(`${palabras} palabras`);
    return `Para poder dictaminar faltan ${trozos.join(' y ')}.`;
  },

  // ── El dictamen ───────────────────────────────────────────────────────────
  juzgando: 'Viendo si lo conseguiste…',
  logrado: '✓ Lo conseguiste',
  noLogrado: '✕ No lo conseguiste',
  sinJuicio: '— Todavía no hay nada que juzgar',
  conTusPalabras: 'Con tus palabras',
  laProxima: 'La próxima vez',
  statTurnos: 'turnos tuyos',
  statPalabras: 'palabras',
  statRitmo: 'palabras/min',
  sinIA: 'No pudimos juzgarlo en este momento, así que no te decimos que sí por si acaso.',
  otraEscena: 'Otra escena →',
};

const en = {
  formato: 'The situation',
  salir: '← Leave',
  volver: 'Back to the room',
  preparando: 'Setting the scene…',

  monta: 'Build your scene',
  montaTexto: 'Pick where you work and which conversation you have to have. At the end nobody grades your English: you get told whether you',
  montaTextoFuerte: 'pulled it off',
  tuSector: 'Your field',
  laConversacion: 'The conversation',
  cuantoAprieta: 'How hard they push',
  suAcento: 'Their accent',
  exigenciaPorque1: 'This changes the other person, not your level. On',
  exigenciaPorqueDuro: 'tough',
  exigenciaPorque2: 'they interrupt you and only give ground if you genuinely convince them.',
  entrar: 'Enter the conversation →',

  catalogo: (n) => `${n} different scenes, and no AI makes any of them up: they `
    + 'are built from fixed pieces, so the same setup gives you the same scene '
    + 'today and a month from now. Your choice is saved for next time.',
  sinMicro: 'This browser cannot hear you, so you will type your turns. The '
    + 'format works the same, but speaking trains you far more — on Chrome or '
    + 'Edge you get a microphone.',

  contestar: '🎤 Reply',
  yaDije: '■ That is my turn',
  enviar: 'Send my turn',
  escribeAqui: 'Type your turn in English…',
  teEscucho: 'Listening…',
  cerrar: 'Close and see if I pulled it off',
  sueloListo: '✓ There is enough conversation to judge now. Close whenever you want.',
  suelo: (turnos, palabras) => {
    const trozos = [];
    if (turnos) trozos.push(`${turnos} more ${turnos === 1 ? 'turn' : 'turns'} from you`);
    if (palabras) trozos.push(`${palabras} more words`);
    return `Before this can be judged you still need ${trozos.join(' and ')}.`;
  },

  juzgando: 'Working out whether you pulled it off…',
  logrado: '✓ You pulled it off',
  noLogrado: '✕ You did not pull it off',
  sinJuicio: '— Nothing to judge yet',
  conTusPalabras: 'In your own words',
  laProxima: 'Next time',
  statTurnos: 'your turns',
  statPalabras: 'words',
  statRitmo: 'words/min',
  sinIA: 'We could not judge it right now, so we are not telling you yes just in case.',
  otraEscena: 'Another scene →',
};

/** Los textos del idioma EFECTIVO, que decide el servidor y manda con el
 *  catálogo. Cualquier cosa que no sea "en" cae en español, que es el punto de
 *  partida de toda la app. */
export function textos(idiomaEfectivo) {
  return idiomaEfectivo === 'en' ? en : es;
}

/** Las dos columnas, para que una prueba pueda compararlas sin importar la
 *  pantalla entera. Si una clave existe en un idioma y no en el otro, el alumno
 *  ve un hueco — y eso se comprueba, no se confía. */
export const columnas = { es, en };
